import { ReactNode, useId } from 'react';
import { LucideIcon } from 'lucide-react';
import { Toggle } from './Toggle';

export interface ToggleRowProps {
  label: ReactNode;
  description?: ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  icon?: LucideIcon;
  /** Akcja po prawej ZAMIAST toggle (np. button "Wyczyść teraz"). */
  action?: ReactNode;
  /** Warning badge pod labelem (np. "Wymaga konfiguracji..."). */
  warning?: ReactNode;
  className?: string;
}

/**
 * Wiersz ustawień: label + description po lewej, toggle po prawej.
 * Najczęstszy wzorzec w ustawieniach — 30+ miejsc w kodzie.
 * Zastępuje inline `flex justify-between bg-dark-800/30 p-3...` wzorce.
 *
 * W Settings page łącz z <SettingsSection flushContent>:
 *   <SettingsSection flushContent title="...">
 *     <ToggleRow label="..." />
 *     <ToggleRow label="..." />
 *   </SettingsSection>
 */
export function ToggleRow({
  label,
  description,
  checked,
  defaultChecked,
  onChange,
  disabled,
  icon: Icon,
  action,
  warning,
  className,
}: ToggleRowProps) {
  const id = useId();
  return (
    <div
      className={[
        'flex items-center justify-between gap-4 px-5 py-4 border-t border-border-subtle',
        disabled ? 'opacity-60' : '',
        className,
      ].filter(Boolean).join(' ')}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {Icon && (
          <div className="shrink-0 w-8 h-8 rounded-lg bg-surface-hover flex items-center justify-center mt-0.5">
            <Icon className="w-4 h-4 text-text-muted" />
          </div>
        )}
        <label htmlFor={action ? undefined : id} className={`min-w-0 flex-1 ${!action && !disabled ? 'cursor-pointer' : ''}`}>
          <div className="text-sm font-medium text-text-primary leading-tight">{label}</div>
          {description && (
            <div className="mt-1 text-xs text-text-muted leading-relaxed">{description}</div>
          )}
          {warning && (
            <div className="mt-1.5 text-xs text-warning leading-relaxed">{warning}</div>
          )}
        </label>
      </div>
      <div className="shrink-0">
        {action ?? (
          <Toggle
            id={id}
            checked={checked}
            defaultChecked={defaultChecked}
            onChange={(e) => onChange?.(e.target.checked)}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}
